<template>
  <v-card flat class="my-0">
    <v-card-text>
      <v-col align-self="center">
        <v-row align="center">
          <v-select
            v-model="field.dynamicTableId"
            v-bind:label="$t('Table')"
            item-text="text"
            item-value="id"
            :items="internalTables"
            :error-messages="dynamicTableIdErrors"
            @input="validator.field.dynamicTableId.$touch()"
            @blur="validator.field.dynamicTableId.$touch()"
            @change="loadColumns"
          >
            <template slot="append-outer">
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-btn
                    v-on="on"
                    color="success"
                    icon
                    @click="onInternalTableButtonClick"
                  >
                    <v-icon>mdi-table-plus</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("InsertData") }}</span>
              </v-tooltip>
            </template>
          </v-select>
        </v-row>
        <v-row align="center">
          <v-radio-group :row="true" v-model="basicMode">
            <v-radio
              v-bind:label="$t('BasicMode')"
              :value="true"
              @change="generateCommand"
            ></v-radio>
            <v-radio
              v-bind:label="$t('AdvancedMode')"
              :value="false"
              @change="generateCommand"
            ></v-radio>
          </v-radio-group>
        </v-row>
        <div v-if="basicMode">
          <v-row align="center">
            <v-select
              v-bind:label="$t('DisplayColumn')"
              multiple
              v-model="field.internalTable.displayColumns"
              :items="columns"
              @change="generateCommand"
            ></v-select>
          </v-row>
          <v-row
            v-for="(filter, index) in field.internalTable.filters"
            :key="index"
            align="center"
            justify="center"
          >
            <v-col cols="1">
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-btn
                    v-on="on"
                    color="error"
                    icon
                    @click="removeFilter(index)"
                  >
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("Remove") }}</span>
              </v-tooltip>
            </v-col>
            <v-col>
              <v-select
                clearable
                v-bind:label="$t('Filter')"
                v-model="filter.filterColumn"
                :items="columns"
                @change="generateCommand"
              ></v-select>
            </v-col>
            <v-col>
              <v-select
                clearable
                v-bind:label="$t('Value')"
                item-text="code"
                item-value="code"
                v-model="filter.filterValue"
                :items="contentTypeFields"
                @change="generateCommand"
              ></v-select>
            </v-col>
            <v-col cols="1">
              <v-tooltip top>
                <template v-slot:activator="{ on }">
                  <v-btn
                    v-on="on"
                    color="primary"
                    icon
                    @click="addFilter"
                    v-if="showAddFilter(index)"
                  >
                    <v-icon>mdi-plus</v-icon>
                  </v-btn>
                </template>
                <span>{{ $t("Add") }}</span>
              </v-tooltip>
            </v-col>
          </v-row>
          <v-row align="center">
            <v-select
              clearable
              v-bind:label="$t('SortBy')"
              :items="columns"
              v-model="field.internalTable.sortColumn"
              @change="generateCommand"
            ></v-select>
          </v-row>
        </div>
        <v-tabs v-if="!basicMode">
          <v-tab>{{ $t("Query") }}</v-tab>
          <v-tab>{{ $t("NoResultQuery") }}</v-tab>
          <v-tab-item>
            <div class="sql-editor">
              <codemirror v-model="field.command" :options="cmOption" />
            </div>
          </v-tab-item>
          <v-tab-item>
            <div class="sql-editor">
              <codemirror v-model="field.noResultQuery" :options="cmOption" />
            </div>
          </v-tab-item>
        </v-tabs>
        <v-row>
          <v-col>
            <v-switch
              v-model="field.multiValue"
              v-bind:label="$t('Multivalue')"
            ></v-switch>
          </v-col>
          <v-col>
            <v-select
              :loading="loading"
              :multiple="field.multiValue"
              v-bind:label="$t('Test')"
              item-text="text"
              item-value="id"
              :items="testItems"
              :ref="`test-${field.code}`"
              clearable
            >
              <template v-slot:append-outer>
                <v-tooltip top>
                  <template v-slot:activator="{ on }">
                    <v-btn small icon @click="test" v-on="on">
                      <v-icon>mdi-database-check</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t("Test") }}</span>
                </v-tooltip>
              </template>
            </v-select>
          </v-col>
        </v-row>
      </v-col>
    </v-card-text>
    <InternalTableModal
      :ref="internalTableModalKey"
      @on-item-saved="getTables"
    />
    <ListFieldParamsModal
      :ref="listFieldParamsModalKey"
      @on-saved="executeTestQuery"
    />
  </v-card>
</template>

<script>
import Utils from "@/ecm/common/Utils";
import InternalTableModal from "@/components/administration/internalTable/InternalTableModal.vue";
import ListFieldParamsModal from "@/components/administration/contentType/fields/ListFieldParamsModal";
import ContentTypeService from "@/services/ContentTypeService";
import InternalTableService from "@/services/InternalTableService";
import InternalTableFilter from "@/ecm/models/contentType/ContentTypeFieldInternalTableFilter";
import { codemirror } from "vue-codemirror";
// import base style
import "codemirror/lib/codemirror.css";
import "codemirror/mode/sql/sql";
import "codemirror/theme/solarized.css";

export default {
  components: {
    codemirror,
    InternalTableModal,
    ListFieldParamsModal,
  },
  props: {
    field: {
      type: Object,
      default: () => ({}),
    },
    contentTypeFields: {
      type: Array,
      default: () => [],
    },
    validator: {
      type: Object,
      default: () => ({}),
    },
  },
  created() {
    this.testItems = [];
    InternalTableService.getList().then((data) => {
      this.internalTables = data;
      if (
        this.field.dynamicTableId &&
        parseInt(this.field.dynamicTableId) > 0
      ) {
        this.loadColumns();
      }
    });
  },
  data() {
    return {
      internalTables: [],
      loading: false,
      testItems: [],
      columns: [],
      basicMode: true,
      internalTableModalKey: "internalTableModalKey",
      listFieldParamsModalKey: "listFieldParamsModalKey",
      cmOption: {
        tabSize: 4,
        styleActiveLine: true,
        lineNumbers: true,
        line: true,
        mode: "text/x-mssql",
        theme: "solarized light",
      },
    };
  },
  computed: {
    internalTableModalInstance() {
      return this.$refs[this.internalTableModalKey];
    },
    dynamicTableIdErrors() {
      const errors = [];
      if (!this.validator.field.dynamicTableId.$dirty) return errors;
      !this.validator.field.dynamicTableId.required &&
        errors.push(this.$t("RequiredField"));
      return errors;
    },
    fieldsForFilter() {
      const errors = [];
      if (!this.validator.field.dynamicTableId.$dirty) return errors;
      !this.validator.field.dynamicTableId.required &&
        errors.push(this.$t("RequiredField"));
      return errors;
    },
    hasFilters() {
      this.field.internalTable.filters.push(new InternalTableFilter());
    },
  },

  watch: {
    "field.internalTable.displayColumns": {
      immediate: true,
      handler(val, oldVal) {
        this.testItems = [];
        debugger;
        if (val && val.length > 0) {
          this.basicMode = true;
        } else {
          this.basicMode = false;
        }
      },
    },
    "field.dynamicTableId": {
      immediate: true,
      handler(val, oldVal) {
        this.testItems = [];
        if (parseInt(val) > 0) {
          this.loadColumns();
        }
      },
    },
  },
  methods: {
    getTables() {
      InternalTableService.getList().then((data) => {
        this.internalTables = data;
        if (
          this.field.dynamicTableId &&
          parseInt(this.field.dynamicTableId) > 0
        ) {
          this.loadColumns();
        }
      });
    },
    onInternalTableButtonClick() {
      this.internalTableModalInstance.new();
    },
    addFilter() {
      this.field.internalTable.filters.push(new InternalTableFilter());
    },
    removeFilter(index) {
      this.field.internalTable.filters.splice(index, 1);
      if (this.field.internalTable.filters.length == 0) {
        this.addFilter();
      }
    },
    showAddFilter(index) {
      return index == this.field.internalTable.filters.length - 1;
    },
    loadColumns() {
      InternalTableService.get(this.field.dynamicTableId).then((data) => {
        this.columns = data.Columns.map(function (column) {
          return column.Name;
        });
        if (this.field.internalTable.displayColumns.length > 0) {
          this.basicMode = true;
        }
      });
    },
    generateCommand() {
      if (!this.basicMode) return;
      if (this.field.internalTable.displayColumns.length == 0) {
        return;
      }

      let columns = "";

      for (var i = 0; i < this.field.internalTable.displayColumns.length; i++) {
        if (i == 0) {
          columns += "[" + this.field.internalTable.displayColumns[i] + "]";
        } else {
          columns +=
            " + ' - ' + [" + this.field.internalTable.displayColumns[i] + "]";
        }
      }

      let tableName = this.internalTables.filter(
        (f) => f.id == this.field.dynamicTableId
      )[0].text;

      let query = `SELECT ${columns} AS VALUE FROM [${tableName}] `;

      for (var i = 0; i < this.field.internalTable.filters.length; i++) {
        if (
          this.field.internalTable.filters[i].filterColumn &&
          this.field.internalTable.filters[i].filterValue
        ) {
          if (i == 0) {
            query += ` WHERE [${this.field.internalTable.filters[i].filterColumn}] = '{${this.field.internalTable.filters[i].filterValue}}' `;
          } else {
            query += ` AND [${this.field.internalTable.filters[i].filterColumn}] = '{${this.field.internalTable.filters[i].filterValue}}' `;
          }
        }
      }

      if (this.field.internalTable.sortColumn) {
        query += ` ORDER BY [${this.field.internalTable.sortColumn}]`;
      }

      this.field.command = query;
    },
    test() {
      this.testItems = [];
      this.generateCommand();
      debugger;
      var paramItems = this.contentTypeFields
        .filter((f) => this.field.command.includes(`{${f.code}}`))
        .map((f) => {
          return {
            code: f.code,
            name: f.name,
            multiValue: f.multiValue,
            value: "",
          };
        });
      if (paramItems.length > 0) {
        this.$refs[this.listFieldParamsModalKey].edit(paramItems);
      } else {
        this.executeTestQuery(paramItems);
      }
    },
    executeTestQuery(paramItems) {
      var test = {
        Query: this.field.command,
        NoResultQuery: this.field.noResultQuery || "",
        InternaTableId: this.field.dynamicTableId,
        Parameters: paramItems.map((p) => {
          return {
            Name: p.code,
            Value: p.value,
            Multivalue: p.multiValue,
          };
        }),
      };
      this.loading = true;
      ContentTypeService.testDynamicList(test)
        .then((data) => {
          Utils.toast.success(this.$t("SuccessfulTest"));
          this.testItems = data;
        })
        .catch((error) => {
          // error
        })
        .then(() => {
          //always
          this.loading = false;
        });
    },
  },
};
</script>

<style lang="scss">
.sql-editor {
  .CodeMirror {
    border: 1px solid #eee;
    height: auto !important;
    min-height: 200px !important;
    /*max-height: 500px;*/
    line-height: 20px;
  }
  .CodeMirror-scroll {
    min-height: 200px;
  }
}
</style>